<template>
  <lb-dialog
    v-model="internalOpen"
    :heading="dialogTitle"
    width="720"
    persistent
    data-testid="ai-context-group-dialog"
  >
    <template v-slot:body>
      <v-container fluid class="pa-0">
        <v-row class="mb-4">
          <v-col cols="12" sm="4" class="font-weight-medium">Category Name :</v-col>
          <v-col cols="12" sm="8">
            <lb-string
              v-model="form.group_name"
              placeholder="Enter category name"
              :error="errors.group_name"
              data-testid="ai-context-group-dialog__group-name"
            />
          </v-col>
        </v-row>

        <div class="ai-context-group-dialog__section mb-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="font-weight-medium">GL Code &amp; Description</span>
            <search-checkbox-picker-menu
              :options="glOptions"
              title="GL Code & Description"
              apply-label="Apply"
              activator-label="+ Add GLs"
              :selected-values="selectedGlIds"
              :module-filters="glModuleFilters"
              :module-filter-value="glModuleFilter"
              test-id="ai-context-group-dialog__gl-picker"
              @open="ensureGlOptionsLoaded"
              @module-filter-change="onGlModuleFilterChange"
              @apply="onGlApply"
            />
          </div>
          <v-alert v-if="errors.gl_codes" type="error" dense text class="mb-2 py-1">
            {{ errors.gl_codes }}
          </v-alert>
          <div class="ai-context-group-dialog__chip-area">
            <AiContextChipList
              :chips="form.gl_codes"
              type="gl"
              removable
              :limit="0"
              show-placeholder
              placeholder="Add GL(s)"
              test-id="ai-context-group-dialog__gl-chips"
              @remove="removeGl"
            />
          </div>
        </div>

        <div class="ai-context-group-dialog__section mb-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="font-weight-medium">Network</span>
            <search-checkbox-picker-menu
              :options="networkOptions"
              title="Network"
              apply-label="Apply"
              activator-label="+ Add Networks"
              :selected-values="selectedNetworkIds"
              test-id="ai-context-group-dialog__network-picker"
              @open="ensureNetworkOptionsLoaded"
              @apply="onNetworkApply"
            />
          </div>
          <v-alert v-if="errors.networks" type="error" dense text class="mb-2 py-1">
            {{ errors.networks }}
          </v-alert>
          <div class="ai-context-group-dialog__chip-area">
            <AiContextChipList
              :chips="form.networks"
              type="network"
              removable
              :limit="0"
              show-placeholder
              placeholder="Add Network(s)"
              test-id="ai-context-group-dialog__network-chips"
              @remove="removeNetwork"
            />
          </div>
        </div>

        <div class="ai-context-group-dialog__section">
          <span class="font-weight-medium d-block mb-2">Context</span>
          <lb-textarea
            v-model="form.context"
            placeholder="Type Here..."
            :error="errors.context"
            data-testid="ai-context-group-dialog__context"
          />
        </div>
      </v-container>
    </template>

    <template v-slot:actions>
      <v-spacer />
      <base-button
        secondary
        label="Cancel"
        class="mr-2"
        @click="close"
        data-testid="ai-context-group-dialog__cancel"
      />
      <base-button
        primary
        :label="mode === 'create' ? 'Create' : 'Update'"
        :loading="saving"
        @click="save"
        data-testid="ai-context-group-dialog__save"
      />
    </template>
  </lb-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import AiContextChipList from './AiContextChipList.vue';
import {
  validateContextGroupForm,
  normalizePickerId,
  reportingTypeFromSuperGroup,
} from '../../../utils/aiContextHelpers';

const emptyForm = () => ({
  group_name: '',
  super_group: 'PL',
  reporting_type: 'pl',
  gl_codes: [],
  networks: [],
  context: '',
});

const GL_MODULE_FILTERS = [
  { text: 'Profit & Loss', value: 'pl' },
  { text: 'Balance Sheet', value: 'bs' },
];

export default {
  name: 'AiContextGroupDialog',
  components: { AiContextChipList },
  props: {
    value: { type: Boolean, default: false },
    mode: { type: String, default: 'create' },
    group: { type: Object, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      errors: {},
      saving: false,
      glModuleFilter: 'pl',
      glOptionsLoaded: false,
      networkOptionsLoaded: false,
    };
  },
  computed: {
    ...mapGetters('varianceanalysis/contextGroups', ['glPickerOptions', 'networkPickerOptions']),
    internalOpen: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },
    dialogTitle() {
      return this.mode === 'create' ? 'Create Group' : 'Edit';
    },
    glOptions() {
      return this.glPickerOptions || [];
    },
    networkOptions() {
      return this.networkPickerOptions || [];
    },
    selectedGlIds() {
      return (this.form.gl_codes || []).map((g) => {
        const id = g.gl_id || g.value || g.code;
        return normalizePickerId(id) || id;
      });
    },
    selectedNetworkIds() {
      return (this.form.networks || []).map((n) => normalizePickerId(n.network_id || n.value));
    },
    glModuleFilters() {
      return GL_MODULE_FILTERS;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetForm();
        this.glOptionsLoaded = false;
        this.networkOptionsLoaded = false;
      }
    },
    group: {
      immediate: true,
      handler(val) {
        if (val && this.value) this.resetForm();
      },
    },
  },
  methods: {
    async ensureGlOptionsLoaded() {
      if (this.glOptionsLoaded && (this.glPickerOptions || []).length) return;
      await this.loadGlOptions(this.glModuleFilter);
    },
    async ensureNetworkOptionsLoaded() {
      if (this.networkOptionsLoaded && (this.networkPickerOptions || []).length) return;
      try {
        await this.$store.dispatch('varianceanalysis/contextGroups/fetchNetworkPickerOptions', {
          axios: this.axios,
        });
        this.networkOptionsLoaded = true;
      } catch (e) {
        // surfaced via store
      }
    },
    async loadGlOptions(reportingType = 'pl') {
      try {
        await this.$store.dispatch('varianceanalysis/contextGroups/fetchGlPickerOptions', {
          axios: this.axios,
          reporting_type: reportingType === 'stat' ? 'pl' : reportingType,
        });
        this.glOptionsLoaded = true;
        this.form.reporting_type = reportingType;
        this.form.super_group = reportingType === 'bs' ? 'BS' : 'PL';
      } catch (e) {
        // surfaced via store
      }
    },
    async onGlModuleFilterChange(value) {
      this.glModuleFilter = value;
      this.glOptionsLoaded = false;
      await this.loadGlOptions(value);
    },
    resetForm() {
      this.errors = {};
      if (this.mode === 'edit' && this.group) {
        const reportingType = this.group.reporting_type
          || reportingTypeFromSuperGroup(this.group.super_group)
          || 'pl';
        this.form = {
          group_name: this.group.group_name || this.group.group_key || '',
          super_group: this.group.super_group || (reportingType === 'bs' ? 'BS' : 'PL'),
          reporting_type: reportingType,
          gl_codes: (this.group.gl_codes || []).map((g) => ({ ...g })),
          networks: (this.group.networks || []).map((n) => ({ ...n })),
          context: this.group.context || '',
        };
        this.glModuleFilter = reportingType === 'bs' ? 'bs' : 'pl';
      } else {
        this.form = emptyForm();
        this.glModuleFilter = 'pl';
      }
    },
    /** Replace selection so deselected items are removed on Apply */
    onGlApply(selected) {
      this.form.gl_codes = (selected || []).map((opt) => ({
        gl_id: opt.gl_id || opt.value,
        code: opt.code || '',
        name: opt.name || opt.text || '',
      }));
      if (this.errors.gl_codes) this.$delete(this.errors, 'gl_codes');
    },
    onNetworkApply(selected) {
      this.form.networks = (selected || []).map((opt) => ({
        network_id: normalizePickerId(opt.network_id || opt.value),
        name: opt.name || opt.text || '',
      }));
      if (this.errors.networks) this.$delete(this.errors, 'networks');
    },
    removeGl(index) {
      this.form.gl_codes.splice(index, 1);
    },
    removeNetwork(index) {
      this.form.networks.splice(index, 1);
    },
    close() {
      this.internalOpen = false;
    },
    async save() {
      const { valid, errors } = validateContextGroupForm(this.form);
      this.errors = errors;
      if (!valid) return;

      this.saving = true;
      try {
        const payload = {
          group_name: this.form.group_name.trim(),
          group_key: this.form.group_name.trim(),
          super_group: this.form.super_group || (this.form.reporting_type === 'bs' ? 'BS' : 'PL'),
          reporting_type: this.form.reporting_type || 'pl',
          gl_codes: this.form.gl_codes,
          networks: this.form.networks,
          context: this.form.context.trim(),
        };
        if (this.mode === 'edit' && this.group?._id) {
          await this.$store.dispatch('varianceanalysis/contextGroups/updateContextGroup', {
            axios: this.axios,
            id: this.group._id,
            payload,
          });
        } else {
          await this.$store.dispatch('varianceanalysis/contextGroups/createContextGroup', {
            axios: this.axios,
            payload,
          });
        }
        this.$emit('saved');
        this.close();
      } catch (e) {
        if (e.validationErrors) {
          this.errors = { ...this.errors, ...e.validationErrors };
        }
      } finally {
        this.saving = false;
      }
    },
  },
  beforeDestroy() {
    this.form = emptyForm();
    this.errors = {};
  },
};
</script>

<style lang="scss" scoped>
@import './AiContextGroupDialog.scss';
</style>
